import { useAuth } from '@clerk/react'
import * as Select from '@radix-ui/react-select'
import {
  AlignLeft,
  Bomb,
  Braces,
  ChevronDown,
  Info,
  LayoutGrid,
  Loader2,
  RotateCcw,
  Settings,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDevice } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { useSessionHistoryData } from '../attempts-pane'
import { useEditorActions } from '../editor-actions'
import type { LanguageValue } from '../editor-languages'
import {
  isExecutionEnabled,
  isLanguageRolledOut,
  LANGUAGE_OPTIONS,
} from '../editor-languages'
import { DEFAULT_SNIPPETS } from '../editor-snippets'
import { useProblemOptional } from '../problem-context'
import { useSessionOptional } from '../session-context'
import { useEditorSettings } from '../settings-modal/editor-settings-modal-context'
import { TimerPopover } from '../timer/timer-popover'
import { useApplyCodeFromAction } from '../use-apply-code-from-action'

interface CodeToolbarProps {
  language: LanguageValue
  onLanguageChange: (value: LanguageValue) => void
}

export function CodeToolbar({ language, onLanguageChange }: CodeToolbarProps) {
  const languageLabel = useMemo(
    () => LANGUAGE_OPTIONS.find((option) => option.value === language)?.label,
    [language],
  )
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)
  const [noSubmissionToast, setNoSubmissionToast] = useState(false)
  const [applyFailedToast, setApplyFailedToast] = useState(false)
  const editorSettings = useEditorSettings()
  const { format, isFormatting } = useEditorActions()
  const { isSignedIn } = useAuth()
  const { isMobile } = useDevice()
  const sessionHistory = useSessionHistoryData()
  const applyCode = useApplyCodeFromAction()
  const sessionContext = useSessionOptional()
  const problem = useProblemOptional()

  const findLastSubmissionCode = useCallback((): string | null => {
    const sessions = sessionHistory?.data?.sessions
    if (!sessions) return null

    // Sessions are ordered most-recent first; within each session,
    // executions are also ordered most-recent first. Find the first
    // execution with mode 'submit' across all sessions.
    for (const session of sessions) {
      for (const exec of session.executions) {
        if (exec.executionMode === 'submit') {
          return exec.code
        }
      }
    }
    return null
  }, [sessionHistory?.data?.sessions])

  const handleResetConfirm = useCallback(async () => {
    setShowResetDialog(false)
    const starter =
      problem?.data?.testConfig?.starterCode?.[language] ??
      DEFAULT_SNIPPETS[language]
    const result = await applyCode({
      code: starter,
      language,
      source: 'toolbar-reset',
    })
    if (!result.ok) {
      setApplyFailedToast(true)
      return
    }
    sessionContext?.notifyRestoreCompleted()
  }, [applyCode, language, problem, sessionContext])

  const handleRestoreConfirm = useCallback(async () => {
    const code = findLastSubmissionCode()
    setShowRestoreDialog(false)
    if (!code) {
      setNoSubmissionToast(true)
      return
    }
    const result = await applyCode({
      code,
      language,
      source: 'toolbar-restore-last',
    })
    if (!result.ok) {
      setApplyFailedToast(true)
      return
    }
    sessionContext?.notifyRestoreCompleted()
  }, [applyCode, findLastSubmissionCode, language, sessionContext])

  // Auto-dismiss the "no submission" toast after 2 seconds
  useEffect(() => {
    if (!noSubmissionToast) return
    const timer = setTimeout(() => setNoSubmissionToast(false), 2000)
    return () => clearTimeout(timer)
  }, [noSubmissionToast])

  // Auto-dismiss the "apply failed" toast after 2 seconds
  useEffect(() => {
    if (!applyFailedToast) return
    const timer = setTimeout(() => setApplyFailedToast(false), 2000)
    return () => clearTimeout(timer)
  }, [applyFailedToast])

  return (
    <header
      className={cn(
        'border-border/60 flex items-center gap-3 border-b px-3 py-2',
        isMobile && 'py-2.5',
      )}
    >
      <nav aria-label="Language selection" className="flex items-center gap-2">
        <Select.Root value={language} onValueChange={onLanguageChange}>
          <Select.Trigger
            aria-label="Select language"
            className={cn(
              'text-foreground hover:bg-muted focus-visible:ring-ring inline-flex items-center gap-1 rounded px-2 text-sm focus:outline-none focus-visible:ring-2',
              isMobile ? 'h-11 min-w-[44px]' : 'h-7',
            )}
          >
            <Select.Value />
            <Select.Icon>
              <ChevronDown className="h-3.5 w-3.5" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              className="border-border/60 bg-background z-50 overflow-hidden rounded-md border shadow-lg"
              position="popper"
            >
              <Select.Viewport className="p-1">
                {LANGUAGE_OPTIONS.map((option) => {
                  const isDisabled = !isExecutionEnabled(
                    option.value,
                    problem?.availableLanguages ?? null,
                  )
                  // Rolled-out languages arrive problem-by-problem as their
                  // artifacts land (LG-247); languages not rolled out at
                  // all are "Coming Soon" everywhere.
                  const disabledNote = isLanguageRolledOut(option.value)
                    ? 'Not yet available for this problem'
                    : 'Coming Soon'
                  return (
                    <Select.Item
                      key={option.value}
                      value={option.value}
                      disabled={isDisabled}
                      className={
                        isDisabled
                          ? 'text-muted-foreground relative flex items-center gap-2 rounded px-2 py-1.5 text-sm outline-none select-none cursor-not-allowed'
                          : 'text-foreground hover:bg-muted focus:bg-muted relative flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none select-none'
                      }
                    >
                      {/* The note must stay OUTSIDE ItemText: Radix mirrors
                          ItemText children into the closed trigger's
                          Select.Value, and a selected language can become
                          disabled when navigating to a problem without it. */}
                      <Select.ItemText>{option.label}</Select.ItemText>
                      {isDisabled && (
                        <span className="text-muted-foreground/60 ml-2 text-xs">
                          {disabledNote}
                        </span>
                      )}
                    </Select.Item>
                  )
                })}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </nav>

      <TimerPopover />

      <nav
        aria-label="Editor actions"
        className={cn(
          'ml-auto flex items-center',
          isMobile
            ? 'gap-1 [&_button]:min-h-[44px] [&_button]:min-w-[44px]'
            : 'gap-2',
        )}
      >
        {isSignedIn && !isMobile && <FeaturebaseBugButton />}
        {editorSettings.hasModal && (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => editorSettings.open('code-editor')}
                aria-label="Editor settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="panel">Editor settings</TooltipContent>
          </Tooltip>
        )}
        {isSignedIn && !isMobile && (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => editorSettings.open('layouts')}
                aria-label="Manage layouts"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="panel">Manage layouts</TooltipContent>
          </Tooltip>
        )}
        {isSignedIn && (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowRestoreDialog(true)}
                aria-label="Restore last submission"
              >
                <Braces className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="panel">
              Restore last submission
            </TooltipContent>
          </Tooltip>
        )}
        {isSignedIn && (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={format}
                disabled={isFormatting}
                aria-label={`Format ${languageLabel ?? 'code'}`}
              >
                {isFormatting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <AlignLeft className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="panel">Format code</TooltipContent>
          </Tooltip>
        )}
        <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowResetDialog(true)}
                aria-label={`Reset ${languageLabel ?? 'code'} to default`}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="panel">
              Reset code workspace and start over
            </TooltipContent>
          </Tooltip>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <Bomb className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription className="mt-2">
                    Your current code will be discarded and you'll have to start
                    over from a blank slate!
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => void handleResetConfirm()}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog
          open={showRestoreDialog}
          onOpenChange={setShowRestoreDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <Info className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription className="mt-2">
                    Your code will be discarded and replaced with your last
                    submission's code!
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => void handleRestoreConfirm()}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </nav>

      {/* No-submission toast */}
      {noSubmissionToast && (
        <div className="border-border bg-background text-foreground animate-in fade-in slide-in-from-top-2 fixed left-1/2 top-16 z-[9999] flex -translate-x-1/2 items-center gap-2 rounded-lg border px-4 py-2.5 shadow-lg">
          <XCircle className="h-4 w-4 shrink-0 text-red-500" />
          <span className="text-sm font-medium">
            You haven't submitted any code!
          </span>
        </div>
      )}

      {/* Apply-failed toast — surfaced when applyCode returns { ok: false } */}
      {applyFailedToast && (
        <div className="border-border bg-background text-foreground animate-in fade-in slide-in-from-top-2 fixed left-1/2 top-16 z-[9999] flex -translate-x-1/2 items-center gap-2 rounded-lg border px-4 py-2.5 shadow-lg">
          <XCircle className="h-4 w-4 shrink-0 text-red-500" />
          <span className="text-sm font-medium">
            Couldn't apply the code. Please try again.
          </span>
        </div>
      )}
    </header>
  )
}
